import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { API_URLS } from '../../config/api';
import { formatPrice, safeParseFloat } from '../../utils/format';
import MediaDisplay from '../MediaDisplay';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import * as Icons from 'lucide-react';
import {
  Server,
  HelpCircle,
  Cpu,
  HardDrive,
  Network,
  CheckCircle,
  ArrowRight,
  Crown,
  BarChart3,
  X as XIcon
} from 'lucide-react';

const resolveIcon = (iconName, fallback = 'Server') => {
  if (iconName && Icons[iconName]) return Icons[iconName];
  if (fallback && Icons[fallback]) return Icons[fallback];
  return Icons.Server;
};

const splitParagraphs = (text) =>
  typeof text === 'string'
    ? text
        .split(/\n+/)
        .map((paragraph) => paragraph.trim())
        .filter(Boolean)
    : [];

const VPSServerPage = () => {
  const [selectedOS, setSelectedOS] = useState('');
  const [vpsPlans, setVpsPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [whatsappNumber, setWhatsappNumber] = useState('905324404011');
  const [contentReady, setContentReady] = useState(false);
  const [pageContent, setPageContent] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [faqsLoading, setFaqsLoading] = useState(false);

  // Fade-in animasyonu için
  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  const fetchContent = async () => {
    try {
      const response = await fetch(API_URLS.PAGE_CONTENT('vps-sunucu'));
      const json = await response.json();
      if (json.status === 'success' && json.data) {
        const additionalRaw = json.data.additional_sections;
        let additional = {};
        if (typeof additionalRaw === 'string' && additionalRaw.trim().startsWith('{')) {
          try {
            additional = JSON.parse(additionalRaw);
          } catch (error) {
            console.error('VPS additional_sections parse error:', error);
            additional = {};
          }
        } else if (additionalRaw && typeof additionalRaw === 'object') {
          additional = additionalRaw;
        }
        setPageContent({
          ...json.data,
          additional_sections: additional
        });
      } else {
        setPageContent(null);
      }
    } catch (error) {
      console.error('VPS sayfa içeriği alınamadı:', error);
      setPageContent(null);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  useEffect(() => {
    const fetchVpsPackages = async () => {
      try {
        const apiUrl = API_URLS.VPS_PACKAGES;
        
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (Array.isArray(data)) {
          // Veritabanı verilerini frontend formatına dönüştür
          const formattedPlans = data.map(pkg => {
            // Parse features - obje yapısını koru
            let rawFeatures = Array.isArray(pkg.features) ? pkg.features : [];
            const parsedFeatures = rawFeatures.map(f => {
              if (typeof f === 'string') {
                return { name: f, text: f, included: true };
              }
              if (f && typeof f === 'object') {
                return {
                  name: f.name || f.text || '',
                  text: f.text || f.name || '',
                  included: f.included !== false
                };
              }
              return { name: String(f), text: String(f), included: true };
            });
            
            return {
              id: pkg.id,
              name: pkg.name,
              cpu: { cores: pkg.cpu_cores, model: pkg.cpu_model },
              ram: pkg.ram,
              storage: pkg.storage,
              bandwidth: pkg.bandwidth,
              price: safeParseFloat(pkg.price),
              originalPrice: safeParseFloat(pkg.original_price),
              discount: pkg.discount,
              color: pkg.color,
              bgColor: pkg.bg_color,
              popular: (pkg.is_popular == 1 || pkg.is_popular === "1" || pkg.is_popular === true),
              features: parsedFeatures
            };
          });
          setVpsPlans(formattedPlans);
        } else {
          console.error('API geçersiz veri döndürdü:', data);
        }
      } catch (error) {
        console.error('VPS paketleri yüklenirken hata:', error);
        setVpsPlans([]);
      } finally {
        setLoading(false);
      }
    };

    fetchVpsPackages();
    fetchSettings();
    fetchFaqs();
  }, []);

  const normalize = (s) => (s || '').toString().toLowerCase()
    .replace(/ı/g,'i').replace(/ü/g,'u').replace(/ö/g,'o')
    .replace(/ş/g,'s').replace(/ç/g,'c').replace(/ğ/g,'g');

  const fetchFaqs = async () => {
    try {
      setFaqsLoading(true);
      const res = await fetch(API_URLS.FAQ);
      const text = await res.text();
      let data; 
      try { 
        data = JSON.parse(text); 
      } catch { 
        data = {}; 
      }

      if (Array.isArray(data?.categories) && Array.isArray(data?.questions)) {
        const cat = data.categories.find(c => {
          const title = normalize(c?.title);
          const slug = normalize(c?.slug);
          return title === 'vps sunucu' || slug === 'vps-sunucu' || title.includes('vps');
        });
        if (cat) {
          const filtered = data.questions
            .filter(q => q.is_active == 1 && q.category_id === cat.id)
            .sort((a,b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
            .map(q => ({ question: q.question || 'Soru', answer: q.answer || '' }));
          setFaqs(filtered);
        } else {
          setFaqs([]);
        }
      } else {
        setFaqs([]);
      }
    } catch (e) {
      console.error('Error fetching VPS FAQs:', e);
      setFaqs([]);
    } finally {
      setFaqsLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=getCompanySettings`);
      if (res.ok) {
        const json = await res.json();
        if (json.status === 'success' && json.data?.whatsapp) {
          setWhatsappNumber(json.data.whatsapp);
        }
      }
    } catch (e) {
      console.error('WhatsApp numarası alınamadı:', e);
    }
  };

  const derivedContent = useMemo(() => {
    const additionalRaw = pageContent?.additional_sections;
    const additional = additionalRaw && typeof additionalRaw === 'object' ? additionalRaw : {};

    const hero = additional.hero && typeof additional.hero === 'object' ? additional.hero : {};
    const packagesSection = additional.packages_section && typeof additional.packages_section === 'object' ? additional.packages_section : {};
    const controlPanelSection = additional.control_panel_section && typeof additional.control_panel_section === 'object' ? additional.control_panel_section : {};
    const osSection = additional.operating_systems_section && typeof additional.operating_systems_section === 'object' ? additional.operating_systems_section : {};
    const windowsSection = additional.windows_section && typeof additional.windows_section === 'object' ? additional.windows_section : {};
    const comparisonSection = additional.comparison_section && typeof additional.comparison_section === 'object' ? additional.comparison_section : {};
    const supportSection = additional.support_section && typeof additional.support_section === 'object' ? additional.support_section : {};
    const faqSection = additional.faq_section && typeof additional.faq_section === 'object' ? additional.faq_section : {};
    const contactSection = additional.contact_section && typeof additional.contact_section === 'object' ? additional.contact_section : {};

    const toParagraphs = (value) => {
      if (Array.isArray(value)) return value.filter((item) => typeof item === 'string' && item.trim().length > 0);
      if (typeof value === 'string') return splitParagraphs(value);
      return [];
    };

    return {
      hero: {
        badge: pageContent?.hero_badge_text ?? '',
        title: pageContent?.hero_title ?? '',
        description: pageContent?.hero_description ?? '',
        highlight_text: hero.highlight_text ?? '',
        image_url: hero.image_url || pageContent?.hero_image_url || '',
        bullets: Array.isArray(hero.bullets) ? hero.bullets : []
      },
      packages_section: {
        title: packagesSection.title ?? '',
        description: packagesSection.description ?? ''
      },
      control_panel_section: {
        title: controlPanelSection.title ?? '',
        subtitle: controlPanelSection.subtitle ?? '',
        cards: Array.isArray(controlPanelSection.cards) ? controlPanelSection.cards : []
      },
      operating_systems_section: {
        title: osSection.title ?? '',
        subtitle: osSection.subtitle ?? '',
        cards: Array.isArray(osSection.cards) ? osSection.cards : []
      },
      windows_section: {
        title: windowsSection.title ?? '',
        description: windowsSection.description ?? '',
        paragraphs: toParagraphs(windowsSection.description),
        highlights: Array.isArray(windowsSection.highlights) ? windowsSection.highlights : [],
        image_url: windowsSection.image_url || '',
        highlight_title: windowsSection.highlight_title ?? '',
        highlight_description: windowsSection.highlight_description ?? ''
      },
      comparison_section: {
        title: comparisonSection.title ?? '',
        description: comparisonSection.description ?? '',
        paragraphs: toParagraphs(comparisonSection.description),
        image_url: comparisonSection.image_url || ''
      },
      support_section: {
        title: supportSection.title ?? '',
        description: supportSection.description ?? '',
        cards: Array.isArray(supportSection.cards) ? supportSection.cards : [],
        highlight: supportSection.highlight && typeof supportSection.highlight === 'object' ? supportSection.highlight : {}
      },
      faq_section: {
        title: faqSection.title ?? '',
        subtitle: faqSection.subtitle ?? '',
        items: Array.isArray(faqSection.items) ? faqSection.items : []
      },
      contact_section: {
        title: contactSection.title ?? '',
        description: contactSection.description ?? '',
        primary_button: contactSection.primary_button && typeof contactSection.primary_button === 'object' ? contactSection.primary_button : {},
        secondary_button: contactSection.secondary_button && typeof contactSection.secondary_button === 'object' ? contactSection.secondary_button : {},
        highlights: Array.isArray(contactSection.highlights) ? contactSection.highlights : []
      }
    };
  }, [pageContent]);

  const heroContent = derivedContent.hero;
  const packagesSection = derivedContent.packages_section;
  const controlPanelContent = derivedContent.control_panel_section;
  const operatingSystemsContent = derivedContent.operating_systems_section;
  const windowsContent = derivedContent.windows_section;
  const comparisonContent = derivedContent.comparison_section;
  const supportContent = derivedContent.support_section;
  const faqContent = derivedContent.faq_section;
  const contactContent = derivedContent.contact_section;
  const controlPanelCards = Array.isArray(controlPanelContent.cards) ? controlPanelContent.cards : [];
  const operatingSystemsCards = Array.isArray(operatingSystemsContent.cards) ? operatingSystemsContent.cards : [];
  const windowsHighlights = Array.isArray(windowsContent.highlights) ? windowsContent.highlights : [];
  const comparisonParagraphs = comparisonContent.paragraphs || [];
  const faqItemsFromContent = Array.isArray(faqContent.items) ? faqContent.items : [];
  
  // Use database FAQs if available, otherwise fall back to page content
  const faqItems = faqs.length > 0 ? faqs : faqItemsFromContent;
  const supportCards = Array.isArray(supportContent.cards) ? supportContent.cards : [];
  const supportHighlight = supportContent.highlight || {};
  const contactHighlights = Array.isArray(contactContent.highlights) ? contactContent.highlights : [];
  const primaryButton = contactContent.primary_button || {};
  const secondaryButton = contactContent.secondary_button || {};
  const SupportHighlightIcon = supportHighlight.icon ? resolveIcon(supportHighlight.icon, 'Headphones') : null;
  const defaultWhatsappMessage = 'Merhaba, VPS sunucu hizmetleriniz hakkında bilgi almak istiyorum.';
  const windowsHighlightTitle = windowsContent.highlight_title;
  const windowsHighlightDescription = windowsContent.highlight_description;
  const heroBadge = heroContent.badge;
  const heroTitleText = heroContent.title;
  const heroDescriptionText = heroContent.description;
  const heroHighlight = heroContent.highlight_text;
  const heroImage = heroContent.image_url;
  const heroBullets = Array.isArray(heroContent.bullets) ? heroContent.bullets : [];

  useEffect(() => {
    if (operatingSystemsCards.length > 0) {
      setSelectedOS((prev) => {
        const exists = operatingSystemsCards.some((os, index) => (os.id || `os-${index}`) === prev);
        if (exists && prev) {
          return prev;
        }
        const first = operatingSystemsCards[0];
        return first?.id || 'os-0';
      });
    }
  }, [operatingSystemsCards]);


  const VPSCard = ({ plan }) => {
    return (
      <Card
        className={`relative border-0 shadow-xl cursor-pointer ${
          plan.popular ? 'ring-4 ring-purple-200' : ''
        } bg-white`}
      >
        {/* Popular Badge */}
        {plan.popular && (
          <div className="absolute top-0 transform -translate-x-1/2 -translate-y-1/2 z-10" style={{ left: '52%' }}>
            <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 text-sm font-bold" style={{ width: '150px' }}>
              <Crown className="w-4 h-4 mr-1" />
              EN POPÜLER
            </Badge>
          </div>
        )}

        <CardHeader className="space-y-4 pb-6">
          <div className="space-y-4">
            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${plan.color} flex items-center justify-center`}>
              <Server className="w-8 h-8 text-white" />
            </div>
            
            <div className="space-y-2">
              <CardTitle className="text-2xl font-bold text-gray-900">
                {plan.name}
              </CardTitle>
              <p className="text-gray-600 leading-relaxed">Yüksek performanslı sanal özel sunucu çözümü</p>
            </div>
          </div>

          {/* Specs Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center space-x-3">
              <Cpu className="w-5 h-5 text-orange-600" />
              <div>
                <p className="font-semibold text-gray-900">{plan.cpu.cores} Çekirdek</p>
                <p className="text-xs text-gray-600">{plan.cpu.model}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <BarChart3 className="w-5 h-5 text-green-600" />
              <div>
                <p className="font-semibold text-gray-900">{plan.ram}</p>
                <p className="text-xs text-gray-600">DDR4 RAM</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <HardDrive className="w-5 h-5 text-purple-600" />
              <div>
                <p className="font-semibold text-gray-900">{plan.storage}</p>
                <p className="text-xs text-gray-600">Enterprise SSD</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Network className="w-5 h-5 text-orange-600" />
              <div>
                <p className="font-semibold text-gray-900">{plan.bandwidth}</p>
                <p className="text-xs text-gray-600">Bant Genişliği</p>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="space-y-2">
            <div className="flex items-baseline space-x-2">
              <span className="text-4xl font-bold text-gray-900">
                {formatPrice(plan.price, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </span>
              <span className="text-gray-600">
                /yıllık
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-lg text-gray-400 line-through">
                {formatPrice(plan.originalPrice, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </span>
              <Badge variant="destructive">
                %{plan.discount} İndirim
              </Badge>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Features */}
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-800">Özellikler:</h4>
            <div className="grid grid-cols-2 gap-2">
              {plan.features.map((feature, idx) => {
                const isIncluded = feature.included !== false;
                // Güvenli string extraction
                const featureText = typeof feature === 'string'
                  ? feature
                  : (feature?.text || feature?.name || String(feature));
                return (
                  <div 
                    key={idx} 
                    className="flex items-center space-x-2"
                  >
                    {isIncluded ? (
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <XIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    )}
                    <span className={`text-xs font-medium ${isIncluded ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                      {featureText}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* CTA Button */}
          <Button 
            className="w-full h-12 font-semibold bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white"
          >
            <span>SATIN AL</span>
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </CardContent>
      </Card>
    );
  };

  const OSSection = () => (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
      {operatingSystems.map((os) => (
        <Card
          key={os.id}
          className={`relative overflow-hidden border-0 shadow-lg cursor-pointer ${
            selectedOS === os.id ? 'ring-2 ring-purple-500' : ''
          }`}
          onClick={() => setSelectedOS(os.id)}
        >
          <CardContent className="relative p-6 text-center">
            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${os.color} flex items-center justify-center mx-auto mb-4`}>
              <os.icon className="w-8 h-8 text-white" />
            </div>
            
            <h3 className="text-lg font-bold text-gray-900 mb-3">{os.name}</h3>
            
            <div className="space-y-2">
              {os.versions.map((version, idx) => (
                <div key={idx} className="flex items-center justify-center space-x-2">
                  <CheckCircle className="w-3 h-3 text-green-500" />
                  <span className="text-sm text-gray-700">{version}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const ControlPanelSection = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {controlPanelFeatures.map((feature) => (
        <Card key={feature.id} className="border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                <feature.icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900">{feature.title}</h3>
            </div>
            <p className="text-sm text-gray-600">{feature.description}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section 
        className="bg-gradient-to-r from-orange-500 to-red-600 text-white py-20"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              {heroBadge && (
                <Badge className="inline-flex items-center gap-2 bg-white/10 text-white border border-white/20 uppercase tracking-wider text-xs font-semibold px-3 py-1 mb-4 mx-auto lg:mx-0">
                  {heroBadge}
                </Badge>
              )}
              {heroTitleText && (
                <h1 className="text-4xl md:text-6xl font-bold mb-6">
                  {heroTitleText}
                </h1>
              )}
              {heroDescriptionText && (
                <p className="text-xl md:text-2xl mb-8">
                  {heroDescriptionText}
                </p>
              )}
              {heroHighlight && (
                <p className="text-sm uppercase tracking-[0.4em] text-yellow-200 mb-4 font-semibold">
                  {heroHighlight}
                </p>
              )}
              {heroBullets.length > 0 && (
                <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                  {heroBullets.map((bullet, index) => {
                    const BulletIcon = resolveIcon(bullet.icon, 'CheckCircle');
                    return (
                      <div key={`hero-bullet-${index}`} className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 text-sm font-semibold">
                        <BulletIcon className="w-4 h-4 text-yellow-300" />
                        <span>{bullet.text}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {heroImage && (
              <div className="hidden lg:block">
                <div className="rounded-2xl bg-white/10 border border-white/10 overflow-hidden">
                  <MediaDisplay
                    src={heroImage}
                    alt={heroTitleText || 'VPS Sunucu'}
                    className="w-full h-full object-cover"
                    videoProps={{ controls: true }}
                    imgProps={{ loading: "lazy" }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* VPS Plans Section */}
      <section 
        className="py-16 bg-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {(packagesSection.title || packagesSection.description) && (
            <div className="text-center mb-12">
              {packagesSection.title && (
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  {packagesSection.title}
                </h2>
              )}
              {packagesSection.description && (
                <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                  {packagesSection.description}
                </p>
              )}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
            </div>
          ) : vpsPlans.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {vpsPlans.map((plan) => (
                <VPSCard key={plan.id || plan.name} plan={plan} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Server className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">VPS Paketleri Yükleniyor</h3>
              <p className="text-gray-600">Paketler hazırlanıyor, lütfen bekleyin...</p>
            </div>
          )}
        </div>
      </section>

      {(controlPanelContent.title || controlPanelContent.subtitle || controlPanelCards.length > 0) && (
        <section 
          className="py-16 bg-gradient-to-r from-orange-50 to-red-50"
          style={{ 
            opacity: contentReady ? 1 : 0, 
            transition: 'opacity 0.6s ease-in' 
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              {controlPanelContent.title && (
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  {controlPanelContent.title}
                </h2>
              )}
              {controlPanelContent.subtitle && (
                <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                  {controlPanelContent.subtitle}
                </p>
              )}
            </div>

            {controlPanelCards.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {controlPanelCards.map((feature, index) => {
                  const FeatureIcon = resolveIcon(feature.icon, 'Settings');
                  return (
                    <Card key={`control-panel-${index}`} className="border-0 shadow-lg">
                      <CardContent className="p-6">
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                            <FeatureIcon className="w-5 h-5 text-white" />
                          </div>
                          <h3 className="font-semibold text-gray-900">{feature.title}</h3>
                        </div>
                        <p className="text-sm text-gray-600">{feature.description}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {(operatingSystemsContent.title || operatingSystemsContent.subtitle || operatingSystemsCards.length > 0) && (
        <section 
          className="py-16 bg-white"
          style={{ 
            opacity: contentReady ? 1 : 0, 
            transition: 'opacity 0.6s ease-in' 
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              {operatingSystemsContent.title && (
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  {operatingSystemsContent.title}
                </h2>
              )}
              {operatingSystemsContent.subtitle && (
                <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                  {operatingSystemsContent.subtitle}
                </p>
              )}
            </div>

            {operatingSystemsCards.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {operatingSystemsCards.map((os, index) => {
                  const OperatingIcon = resolveIcon(os.icon, 'Server');
                  const versions = Array.isArray(os.versions)
                    ? os.versions.filter((item) => typeof item === 'string' && item.trim().length > 0)
                    : splitParagraphs(os.version_text || '');
                  const osId = os.id || `os-${index}`;
                  const gradient = os.color || 'from-orange-500 to-red-500';
                  return (
                    <Card
                      key={osId}
                      className={`relative overflow-hidden border-0 shadow-lg cursor-pointer ${
                        selectedOS === osId ? 'ring-2 ring-purple-500' : ''
                      }`}
                      onClick={() => setSelectedOS(osId)}
                    >
                      <CardContent className="relative p-6 text-center">
                        <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center mx-auto mb-4`}>
                          <OperatingIcon className="w-8 h-8 text-white" />
                        </div>
                        
                        <h3 className="text-lg font-bold text-gray-900 mb-3">{os.name}</h3>
                        
                        <div className="space-y-2">
                          {versions.map((version, versionIndex) => (
                            <div key={`${osId}-version-${versionIndex}`} className="flex items-center justify-center space-x-2">
                              <CheckCircle className="w-3 h-3 text-green-500" />
                              <span className="text-sm text-gray-700">{version}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {(windowsContent.title || windowsContent.paragraphs?.length || windowsHighlights.length > 0 || windowsContent.image_url) && (
        <section 
          className="py-16 bg-gray-50"
          style={{ 
            opacity: contentReady ? 1 : 0, 
            transition: 'opacity 0.6s ease-in' 
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center flex-col-reverse lg:grid">
              {windowsContent.image_url && (
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg overflow-hidden">
                  <MediaDisplay
                    src={windowsContent.image_url}
                    alt={windowsContent.title || 'Windows VPS'}
                    className="w-full h-full object-cover"
                    videoProps={{ controls: true }}
                    imgProps={{ loading: "lazy" }}
                  />
                </div>
              )}
              <div className="text-center lg:text-left">
                {windowsContent.title && (
                  <h2 className="text-3xl font-bold text-gray-800 mb-6">
                    {windowsContent.title}
                  </h2>
                )}
                <div>
                  {windowsContent.paragraphs?.map((paragraph, index) => (
                    <p key={`windows-paragraph-${index}`} className="text-lg text-gray-600 mb-4">
                      {paragraph}
                    </p>
                  ))}
                </div>
                {(windowsHighlightTitle || windowsHighlightDescription || windowsHighlights.length > 0) && (
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-6">
                    {windowsHighlightTitle && (
                      <h3 className="text-xl font-semibold text-gray-800 mb-4">{windowsHighlightTitle}</h3>
                    )}
                    {windowsHighlightDescription && (
                      <p className="text-gray-700 mb-4">{windowsHighlightDescription}</p>
                    )}
                    {windowsHighlights.length > 0 && (
                      <ul className="space-y-2">
                        {windowsHighlights.map((highlight, index) => {
                          const HighlightIcon = resolveIcon(highlight.icon, 'CheckCircle');
                          return (
                            <li key={`windows-highlight-${index}`} className="flex items-center justify-center lg:justify-start">
                              <HighlightIcon className="w-5 h-5 text-green-500 mr-3" />
                              <span className="text-gray-700">{highlight.text || highlight.title}</span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {(comparisonContent.title || comparisonParagraphs.length || comparisonContent.image_url) && (
        <section 
          className="py-16 bg-white"
          style={{ 
            opacity: contentReady ? 1 : 0, 
            transition: 'opacity 0.6s ease-in' 
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="text-center lg:text-left order-2 lg:order-1">
                {comparisonContent.title && (
                  <h2 className="text-3xl font-bold text-gray-800 mb-6">
                    {comparisonContent.title}
                  </h2>
                )}
                <div>
                  {comparisonParagraphs.map((paragraph, index) => (
                    <p key={`comparison-paragraph-${index}`} className="text-lg text-gray-600 mb-4">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
              {comparisonContent.image_url && (
                <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg overflow-hidden order-1 lg:order-2">
                  <MediaDisplay
                    src={comparisonContent.image_url}
                    alt={comparisonContent.title || 'VPS ile VDS Arasındaki Farklar'}
                    className="w-full h-full object-cover"
                    videoProps={{ controls: true }}
                    imgProps={{ loading: "lazy" }}
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {(supportContent.title || supportContent.description || supportCards.length > 0 || supportHighlight.title) && (
        <section 
          className="py-16 bg-gradient-to-r from-orange-50 to-red-50"
          style={{ 
            opacity: contentReady ? 1 : 0, 
            transition: 'opacity 0.6s ease-in' 
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="text-center lg:text-left">
                {supportContent.title && (
                  <h2 className="text-3xl font-bold text-gray-800 mb-6">
                    {supportContent.title}
                  </h2>
                )}
                {supportContent.description && (
                  <p className="text-lg text-gray-600 mb-8">
                    {supportContent.description}
                  </p>
                )}
                {supportCards.length > 0 && (
                  <div className="grid grid-cols-2 gap-4">
                    {supportCards.map((card, index) => {
                      const SupportIcon = resolveIcon(card.icon, 'HelpCircle');
                      return (
                        <div key={`support-card-${index}`} className="flex flex-col lg:flex-row items-center lg:items-start space-y-2 lg:space-y-0 lg:space-x-3 p-4 bg-white rounded-lg shadow-md text-center lg:text-left">
                          <SupportIcon className="w-8 h-8 text-orange-600" />
                          <div>
                            <h4 className="font-semibold text-gray-900">{card.title}</h4>
                            <p className="text-sm text-gray-600">{card.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {(supportHighlight.title || supportHighlight.description) && (
                <div className="relative">
                  <div className="bg-white rounded-lg p-8 shadow-xl">
                    <div className="text-center">
                      {SupportHighlightIcon && (
                        <div className="flex items-center justify-center">
                          <SupportHighlightIcon className="w-16 h-16 text-orange-600 mb-4" />
                        </div>
                      )}
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">{supportHighlight.title}</h3>
                      <p className="text-gray-600 mb-6">
                        {supportHighlight.description}
                      </p>
                      {supportHighlight.button_text && supportHighlight.button_link && (
                        supportHighlight.button_link.startsWith('http') ? (
                          <a href={supportHighlight.button_link} target="_blank" rel="noopener noreferrer">
                            <Button className="bg-orange-600 hover:bg-orange-700 text-white">
                              {supportHighlight.button_text}
                            </Button>
                          </a>
                        ) : (
                          <Link to={supportHighlight.button_link}>
                            <Button className="bg-orange-600 hover:bg-orange-700 text-white">
                              {supportHighlight.button_text}
                            </Button>
                          </Link>
                        )
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {(faqContent.title || faqContent.subtitle || faqItems.length > 0) && (
        <section 
          className="py-16 bg-gray-50"
          style={{ 
            opacity: contentReady ? 1 : 0, 
            transition: 'opacity 0.6s ease-in' 
          }}
        >
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              {(faqContent.title || faqs.length > 0) && (
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  {faqContent.title || 'Sıkça Sorulan Sorular'}
                </h2>
              )}
              {(faqContent.subtitle || faqs.length > 0) && (
                <p className="text-gray-600 text-lg max-w-3xl mx-auto">
                  {faqContent.subtitle || 'VPS Sunucu ile ilgili merak ettiğiniz soruların cevaplarını burada bulabilirsiniz'}
                </p>
              )}
            </div>

            {faqsLoading ? (
              <div className="text-center p-8 bg-white rounded-xl">
                <p className="text-gray-500">Yükleniyor...</p>
              </div>
            ) : faqItems.length > 0 ? (
              <div className="max-w-4xl mx-auto">
                <Accordion type="single" collapsible className="w-full">
                  {faqItems.map((faq, index) => (
                    <AccordionItem key={`faq-item-${index}`} value={`item-${index}`}>
                      <AccordionTrigger className="text-left">
                        <div className="flex items-center">
                          <HelpCircle className="w-5 h-5 mr-3 text-orange-600" />
                          {faq.question}
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-600">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ) : null}
          </div>
        </section>
      )}

      {(contactContent.title || contactContent.description || primaryButton.text || secondaryButton.text || contactHighlights.length > 0) && (
        <section 
          className="py-20 bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 text-white"
          style={{ 
            opacity: contentReady ? 1 : 0, 
            transition: 'opacity 0.6s ease-in' 
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              {contactContent.title && (
                <h2 className="text-4xl font-bold mb-4">{contactContent.title}</h2>
              )}
              {contactContent.description && (
                <p className="text-xl text-orange-100 mb-8 max-w-3xl mx-auto">
                  {contactContent.description}
                </p>
              )}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                {primaryButton.text && (
                  (() => {
                    const PrimaryIcon = resolveIcon(primaryButton.icon, 'Mail');
                    const link = primaryButton.link || '#';
                    const isExternal = link?.startsWith('http');
                    return (
                      <a
                        href={link}
                        target={isExternal ? '_blank' : undefined}
                        rel={isExternal ? 'noopener noreferrer' : undefined}
                        className="inline-flex items-center px-8 py-4 bg-white text-orange-600 rounded-lg hover:bg-gray-100 font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
                      >
                        <PrimaryIcon className="w-5 h-5 mr-2" />
                        {primaryButton.text}
                      </a>
                    );
                  })()
                )}
                {secondaryButton.text && (
                  (() => {
                    const SecondaryIcon = resolveIcon(secondaryButton.icon, 'MessageCircle');
                    const secondaryAction = (secondaryButton.action || '').toLowerCase();
                    const secondaryHref =
                      secondaryAction === 'whatsapp'
                        ? `https://api.whatsapp.com/send/?phone=${encodeURIComponent(secondaryButton.phone || whatsappNumber)}&text=${encodeURIComponent(secondaryButton.prefill_message || defaultWhatsappMessage)}`
                        : (secondaryButton.link || '#');
                    const isExternal = secondaryAction === 'whatsapp' || (secondaryButton.link && secondaryButton.link.startsWith('http'));
                    return (
                      <a
                        href={secondaryHref}
                        target={isExternal ? '_blank' : undefined}
                        rel={isExternal ? 'noopener noreferrer' : undefined}
                        className={`inline-flex items-center px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 transform hover:scale-105 border-2 shadow-lg ${
                          secondaryAction === 'whatsapp'
                            ? 'bg-green-600 text-white hover:bg-green-700 border-white'
                            : 'bg-white text-orange-600 hover:bg-gray-100 border-transparent'
                        }`}
                      >
                        <SecondaryIcon className="w-5 h-5 mr-2" />
                        {secondaryButton.text}
                      </a>
                    );
                  })()
                )}
              </div>
              {contactHighlights.length > 0 && (
                <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
                  {contactHighlights.map((highlight, index) => {
                    const HighlightIcon = resolveIcon(highlight.icon, 'Shield');
                    return (
                      <div key={`contact-highlight-${index}`} className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                        <HighlightIcon className="w-12 h-12 mx-auto mb-4" />
                        <h3 className="font-semibold text-lg mb-2">{highlight.title}</h3>
                        <p className="text-orange-100">{highlight.description}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default VPSServerPage;
